import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { GjeldendeSvar } from '../../api/reise';

interface Props {
  svar: GjeldendeSvar[];
  onEndre: (sporsmalId: string, instans: string) => void;
  /** Bare svarene i denne modulen, når kunden har valgt en ferdig modul i fremdriften (T104). */
  modul?: string | null;
  onVisAlle?: () => void;
}

/** Svaret slik kunden kjenner det igjen: tekstene til valgene, ikke kodene. */
export function visSvar(s: GjeldendeSvar, t: (nokkel: string, verdier?: Record<string, unknown>) => string): string {
  const tekst = (kode: string) => (kode === 'annet' ? t('reise.oversikt.annet', { tekst: s.kundensOrd ?? '' }) : s.alternativer.find((a) => a.verdi === kode)?.tekst ?? kode);
  const v = s.verdi;

  if (v === null) return t('reise.oversikt.hoppetOver');
  switch (s.type) {
    case 'ett_valg':
      return typeof v === 'string' ? tekst(v) : '';
    case 'flere_valg':
    case 'sjekkliste':
    case 'scenario':
      return Array.isArray(v) ? (v as unknown[]).filter((k): k is string => typeof k === 'string').map(tekst).join(', ') : '';
    case 'tabell':
      return t('reise.oversikt.rader', { antall: Array.isArray(v) ? v.length : 0 });
    case 'policy': {
      const p = (v ?? {}) as { omfang?: string; kjennetegn?: string };
      const omfang = p.omfang ? t(`reise.policy.${p.omfang}`) : '';
      return p.omfang === 'noen' && p.kjennetegn ? `${omfang}: ${p.kjennetegn}` : omfang;
    }
    case 'fil':
      return t('reise.oversikt.fil');
    default:
      return String(v);
  }
}

/**
 * Alle svarene kunden har tilgang til, gruppert per modul. Svar som må bekreftes på nytt, er markert og
 * telt opp øverst. Hvert svar kan endres.
 */
export default function SvarOversikt({ svar: alle, onEndre, modul, onVisAlle }: Props) {
  const { t } = useTranslation();
  const svar = modul ? alle.filter((s) => s.modulId === modul) : alle;
  const tilBekreftelse = svar.filter((s) => s.status === 'bekreft_pa_nytt').length;
  const moduler = svar.reduce<{ id: string; tittel: string; svar: GjeldendeSvar[] }[]>((grupper, s) => {
    const gruppe = grupper.find((g) => g.id === s.modulId);
    if (gruppe) gruppe.svar.push(s);
    else grupper.push({ id: s.modulId, tittel: s.modulTittel, svar: [s] });
    return grupper;
  }, []);

  return (
    <section aria-labelledby="oversikt-tittel">
      <h2 id="oversikt-tittel" tabIndex={-1} className="mb-3 text-xl font-semibold">
        {t('reise.oversikt.tittel')}
      </h2>
      {modul && onVisAlle && (
        <button type="button" onClick={onVisAlle} className="knapp-stille mb-4 px-0">
          {t('reise.oversikt.visAlle')}
        </button>
      )}
      {tilBekreftelse > 0 && (
        <p className="mb-4 flex items-center gap-2 melding-advarsel">
          <AlertTriangle aria-hidden="true" className="h-5 w-5 shrink-0" />
          {t('reise.oversikt.bekreftAntall', { antall: tilBekreftelse })}
        </p>
      )}
      {svar.length === 0 && <p className="text-slate-700">{t('reise.oversikt.ingen')}</p>}

      {moduler.map((m) => (
        <div key={m.id} className="mb-6">
          <h3 className="mb-2 font-semibold text-slate-800">{m.tittel}</h3>
          <ul className="flex flex-col gap-2">
            {m.svar.map((s) => (
              <li
                key={`${s.sporsmalId}|${s.instans}`}
                className={
                  'flex flex-col gap-2 rounded border bg-white p-3 sm:flex-row sm:items-start sm:justify-between ' +
                  (s.status === 'bekreft_pa_nytt' ? 'border-amber-400' : 'border-slate-200')
                }
              >
                <div className="min-w-0">
                  {s.profil && <p className="text-sm text-slate-600">{t('reise.oversikt.profil', { profil: s.profil })}</p>}
                  <p className="font-medium">{s.tekst}</p>
                  <p className="break-words text-slate-700">{visSvar(s, t)}</p>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {s.status === 'bekreft_pa_nytt' && (
                      <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">{t('reise.oversikt.bekreftPaNytt')}</span>
                    )}
                    {s.vetIkke && <span className="merke">{t('reise.oversikt.standardvalg')}</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onEndre(s.sporsmalId, s.instans)}
                  aria-label={t('reise.oversikt.endreSvar', { sporsmal: s.tekst })}
                  className="knapp-sekundaer shrink-0"
                >
                  {t('reise.oversikt.endre')}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
